import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProMarketingLink from './ProMarketingLink';
import { proMarketingInternalHref } from '../SEO/marketingCanonicalUrl';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';
import {
  FR_MARKETING_METIER_NAV_LINKS,
  FR_MARKETING_METIER_PATHS,
  localizedMetierNavPath,
  localizedHomePath,
  localizedHomeAnchorPath,
} from '../../config/marketingMetierNav';
import { getLocaleFromPath } from '../../config/localeRoutes';
import MarketingCTA from './MarketingCTA';
import Logo from '../Logo/Logo';
import PromoBar from './PromoBar';
import {
  ACACED_BANNER_CTA_LABEL,
  ACACED_BANNER_MESSAGE,
  ACACED_PAGE_PATH,
} from '../../config/acacedOffer';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { HELP_CENTER_URL } from '../../config/helpCenter';
import { loginPathForLocale, signupHrefForLocale } from '../../utils/signupLangHref';
import './MarketingHeader.css';

interface MarketingHeaderProps {
  transparent?: boolean;
  lightText?: boolean;
}

const navLinks = [
  { id: 'blog', to: '/blog' },
  { id: 'partners', to: '/partenaires' },
  { id: 'pricing', anchor: 'tarifs' },
  { id: 'faq', href: HELP_CENTER_URL },
] as const;

const LANDING_PATHS = ['/', '/es', '/en', '/de', '/it', '/v2'];

export default function MarketingHeader({ transparent = true, lightText = false }: MarketingHeaderProps) {
  const { t, i18n } = useTranslation(['marketing', 'common']);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [metiersMenuOpen, setMetiersMenuOpen] = useState(false);
  const [promoDismissed, setPromoDismissed] = useState(false);
  const [menuTop, setMenuTop] = useState(64);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  // #1084 v3 : survol puis clic (geste souris normal) refermait le menu ouvert au survol.
  const metiersOpenedByHover = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isLandingPage = LANDING_PATHS.includes(location.pathname);
  // #1120 v3 : l'offre ACACED (diplôme français, bandeau rédigé en FR) n'est affichée que sur les pages FR.
  const pathLocale = getLocaleFromPath(location.pathname);
  const isFrPage = !pathLocale || pathLocale === 'fr';
  const showPromoBar =
    !promoDismissed &&
    isFrPage &&
    (isLandingPage || FR_MARKETING_METIER_PATHS.includes(location.pathname));
  const anchorOnCurrentPage = isLandingPage || FR_MARKETING_METIER_PATHS.includes(location.pathname);

  const syncMenuTop = useCallback(() => {
    const height = headerContainerRef.current?.getBoundingClientRect().height;
    if (height && height > 0) {
      setMenuTop(Math.ceil(height));
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMetiersMenuOpen(false);
    setPromoDismissed(false);
  }, [location.pathname]);

  useEffect(() => {
    syncMenuTop();
    window.addEventListener('resize', syncMenuTop);
    const el = headerContainerRef.current;
    let ro: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => syncMenuTop());
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', syncMenuTop);
      ro?.disconnect();
    };
  }, [syncMenuTop, showPromoBar]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    syncMenuTop();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [mobileMenuOpen, syncMenuTop]);

  const handleAnchorClick = (anchor: string) => {
    if (anchorOnCurrentPage) {
      const el = document.getElementById(anchor);
      if (el) {
        const headerHeight = headerContainerRef.current?.getBoundingClientRect().height ?? 0;
        const y = el.getBoundingClientRect().top + window.scrollY - headerHeight - 24;
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        setMobileMenuOpen(false);
        return;
      }
    }
    navigate(localizedHomeAnchorPath(anchor, location.pathname));
    setMobileMenuOpen(false);
  };

  const isVisible = scrolled || !transparent;

  const containerClass = [
    'mk-header-container',
    !showPromoBar ? 'mk-header-container--no-promo' : '',
    !showPromoBar && isVisible ? 'mk-header-container--solid-safe' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div ref={headerContainerRef} className={containerClass}>
        {showPromoBar && (
          <PromoBar
            message={ACACED_BANNER_MESSAGE}
            ctaLabel={ACACED_BANNER_CTA_LABEL}
            ctaHref={ACACED_PAGE_PATH}
            onDismiss={() => {
              setPromoDismissed(true);
              requestAnimationFrame(() => syncMenuTop());
            }}
          />
        )}
        <header
          className={`mk-header ${(isVisible || mobileMenuOpen) ? 'mk-header--solid' : 'mk-header--transparent'} ${(lightText && !scrolled && !mobileMenuOpen) ? 'mk-header--light-text' : ''}`}
        >
          <div className="mk-header__inner">
            <ProMarketingLink to={localizedHomePath(location.pathname)} className="mk-header__logo-wrapper" onClick={() => setMobileMenuOpen(false)}>
              <Logo fontSize="1.8rem" contrast={lightText && !scrolled && !mobileMenuOpen} />
            </ProMarketingLink>

            {/* Desktop nav */}
            <nav className="mk-header__nav">
              <div
                className={`mk-header__nav-dropdown${metiersMenuOpen ? ' mk-header__nav-dropdown--open' : ''}`}
                onMouseLeave={() => {
                  metiersOpenedByHover.current = false;
                  setMetiersMenuOpen(false);
                }}
              >
                <button
                  type="button"
                  className="mk-header__nav-link mk-header__nav-dropdown-trigger"
                  aria-expanded={metiersMenuOpen}
                  aria-haspopup="true"
                  onClick={() => {
                    if (metiersOpenedByHover.current) {
                      metiersOpenedByHover.current = false;
                      setMetiersMenuOpen(true);
                      return;
                    }
                    setMetiersMenuOpen((open) => !open);
                  }}
                  onMouseEnter={() => {
                    if (!metiersMenuOpen) metiersOpenedByHover.current = true;
                    setMetiersMenuOpen(true);
                  }}
                >
                  {t('marketingHeader.nav.metiers')}
                  <ChevronDown size={16} aria-hidden />
                </button>
                {metiersMenuOpen ? (
                  <ul className="mk-header__nav-dropdown-menu" role="menu">
                    {FR_MARKETING_METIER_NAV_LINKS.map((item) => (
                      <li key={item.path} role="none">
                        <ProMarketingLink
                          to={localizedMetierNavPath(item.path, location.pathname)}
                          className="mk-header__nav-dropdown-link"
                          role="menuitem"
                          onClick={() => setMetiersMenuOpen(false)}
                        >
                          {t(item.labelKey)}
                        </ProMarketingLink>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
              {navLinks.map((link) =>
                'to' in link ? (
                  <ProMarketingLink key={link.id} to={proMarketingInternalHref(link.to)} className="mk-header__nav-link">
                    {t(`marketingHeader.nav.${link.id}`)}
                  </ProMarketingLink>
                ) : 'href' in link ? (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mk-header__nav-link"
                  >
                    {t(`marketingHeader.nav.${link.id}`)}
                  </a>
                ) : (
                  <button
                    key={link.id}
                    type="button"
                    className="mk-header__nav-link mk-header__nav-button"
                    onClick={() => handleAnchorClick(link.anchor!)}
                  >
                    {t(`marketingHeader.nav.${link.id}`)}
                  </button>
                )
              )}
            </nav>

            <div className="mk-header__actions">
              <div className="mk-header__lang">
                <LanguageSwitcher variant="dropdown" />
              </div>
              <button
                type="button"
                className="mk-header__login"
                onClick={() => navigate(loginPathForLocale(i18n.language))}
              >
                {t('marketingHeader.login')}
              </button>
              <MarketingCTA
                label={t('marketingHeader.freeTrial')}
                size="md"
                className="mk-header__cta"
                trackEvent="Header CTA"
              />
            </div>

            {/* Mobile hamburger */}
            <button
              type="button"
              className="mk-header__hamburger"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t('mobileHeader.menu', { ns: 'common' })}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </header>
      </div>

      {/* Mobile drawer — DOM : CTAs → pages → langue (dropdown, pas grille) */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="mk-header__mobile-menu"
            style={{ top: menuTop }}
          >
            <div className="mk-header__mobile-actions">
              <MarketingCTA
                label={t('marketingHeader.freeTrial')}
                size="lg"
                fullWidth
                trackEvent="Header CTA Mobile"
                onClick={() => {
                  navigate(signupHrefForLocale(i18n.language));
                  setMobileMenuOpen(false);
                }}
              />
              <button
                type="button"
                className="mk-header__mobile-login"
                onClick={() => {
                  navigate(loginPathForLocale(i18n.language));
                  setMobileMenuOpen(false);
                }}
              >
                {t('marketingHeader.login')}
              </button>
            </div>

            <nav className="mk-header__mobile-nav" aria-label={t('mobileHeader.menu', { ns: 'common' })}>
              <div className="mk-header__mobile-metiers">
                <div className="mk-header__mobile-nav-heading">
                  {t('marketingHeader.nav.metiers')}
                </div>
                {FR_MARKETING_METIER_NAV_LINKS.map((item) => (
                  <ProMarketingLink
                    key={item.path}
                    to={localizedMetierNavPath(item.path, location.pathname)}
                    className="mk-header__mobile-link"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(item.labelKey)}
                  </ProMarketingLink>
                ))}
              </div>
              {navLinks.map((link) =>
                'to' in link ? (
                  <ProMarketingLink
                    key={link.id}
                    to={proMarketingInternalHref(link.to)}
                    className="mk-header__mobile-link"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(`marketingHeader.nav.${link.id}`)}
                  </ProMarketingLink>
                ) : 'href' in link ? (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mk-header__mobile-link"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t(`marketingHeader.nav.${link.id}`)}
                  </a>
                ) : (
                  <button
                    key={link.id}
                    type="button"
                    className="mk-header__mobile-link mk-header__mobile-button"
                    onClick={() => handleAnchorClick(link.anchor!)}
                  >
                    {t(`marketingHeader.nav.${link.id}`)}
                  </button>
                )
              )}
            </nav>

            <div className="mk-header__mobile-lang">
              <LanguageSwitcher variant="dropdown" showLabel />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
